import { Box, HStack, Table, Thead, Tbody, Tr, Th, Td, Text, Progress } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { SortOrder } from '@duke-hq/lib/pagination';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import type { SortFilters } from './ContributorsLeaderboard';
import { AssignPointsModal } from '../AssignPointsModal';
import { CurrentUserRankingRow } from './CurrentUserRankingRow';

type ContributorsLeaderboardTableProps = {
  balances?: RouterOutputs['groupRewards']['listContributorsLeaderboardWithInfiniteScroll']['items'];
  urlParams: SortFilters;
  onFilters: (filters: SortFilters) => void;
  currentUser?: RouterOutputs['groupRewards']['listContributorsLeaderboardWithInfiniteScroll']['currentUser'];
};

const currency = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});

const ContributorsLeaderboardRow: React.FC<{
  balance: RouterOutputs['groupRewards']['listContributorsLeaderboardWithInfiniteScroll']['items'][number];
}> = React.memo(({ balance }) => {
  const auth = useAuthTenant();
  const isCurrentUser = auth.user.drn === balance.userDrn;

  return (
    <Tr
      bg={isCurrentUser ? 'blue.50' : 'transparent'}
      _hover={{ bg: isCurrentUser ? 'blue.100' : 'bg.subtle' }}
      shadow={isCurrentUser ? 'md' : 'none'}
      borderTop={isCurrentUser ? '2px' : 'none'}
      borderBottom={isCurrentUser ? '2px' : 'none'}
      borderColor={isCurrentUser ? 'blue.300' : 'transparent'}
    >
      <Td width="70px">
        <Box
          width="28px"
          height="28px"
          borderRadius="50%"
          bg="teal.500"
          color="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="xs"
          fontWeight="bold"
        >
          {balance.rank}
        </Box>
      </Td>
      <Td>
        <HStack spacing={4}>
          <UserAvatar
            user={{
              first_name: balance?.firstName != null ? balance.firstName : '',
              last_name: balance?.lastName != null ? balance.lastName : '',
              picture_url: balance?.pictureUrl != null ? balance.pictureUrl : null,
            }}
            width="32px"
            height="32px"
            boxSize="4"
            fontSize="xs"
          />
          <Text fontSize="sm" fontWeight="semibold" color={isCurrentUser ? 'blue.700' : 'inherit'}>
            {[balance.firstName ?? '', balance.lastName ?? ''].filter(Boolean).join(' ')}{' '}
            {isCurrentUser ? (
              <Text as="span" fontSize="xs" color="blue.500">
                <Trans>(You)</Trans>
              </Text>
            ) : null}
          </Text>
        </HStack>
      </Td>
      <Td>
        <HStack justify="flex-end">
          {balance.deletedAt != null ? (
            <AssignPointsModal
              user={{
                drn: balance.userDrn,
                firstName: balance.firstName ?? '',
                lastName: balance.lastName ?? '',
                pictureUrl: balance.pictureUrl ?? null,
                totalPoints: balance.balance ?? 0,
              }}
            />
          ) : (
            <div>&nbsp;</div>
          )}
        </HStack>
      </Td>
      <Td>
        <HStack spacing={3} justify="flex-end">
          <Text
            fontSize="sm"
            fontWeight={isCurrentUser ? 'bold' : 'normal'}
            color={isCurrentUser ? 'blue.700' : 'inherit'}
          >
            {balance.balance ?? 0}
          </Text>
        </HStack>
      </Td>
      <Td>
        <HStack spacing={3} justify="flex-end">
          <Text
            fontSize="sm"
            color={isCurrentUser ? 'green.600' : 'green.500'}
            fontWeight="bold"
            minWidth="40px"
          >
            {currency.format(balance.spent ?? 0)}
          </Text>
          <Progress
            value={balance.earned ? ((balance.spent ?? 0) / balance.earned) * 100 : 0}
            size="sm"
            width="100px"
            borderRadius="full"
            colorScheme={isCurrentUser ? 'blue' : 'brand'}
          />
        </HStack>
      </Td>
    </Tr>
  );
});

ContributorsLeaderboardRow.displayName = 'ContributorsLeaderboardRow';

export function ContributorsLeaderboardTable({
  balances = [],
  urlParams,
  onFilters,
  currentUser,
}: ContributorsLeaderboardTableProps) {
  const { sort, orderBy } = urlParams;

  const handleSort = useCallback(
    (orderBy: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      onFilters({ ...urlParams, sort: newSortDirection, orderBy });
    },
    [onFilters, sort, urlParams],
  );

  return (
    <Table>
      <Thead position="sticky" top={0} bg="white" zIndex={1} boxShadow="sm">
        <Tr>
          <Th>
            <Trans>Rank</Trans>
          </Th>
          <Th>
            <Trans>Name</Trans>
          </Th>
          <Th>&nbsp;</Th>
          <Th>
            <HStack justify="flex-end">
              <SortableHeader
                label={t`Unallocated points`}
                columnName="unallocated_points"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </HStack>
          </Th>
          <Th>
            <HStack justify="flex-end">
              <SortableHeader
                label={t`Total contributions`}
                columnName="total_spent"
                currentOrderBy={orderBy}
                currentSortOrder={sort}
                onSortChange={handleSort}
              />
            </HStack>
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(balances) && balances.length
          ? balances.map((balance) => (
              <ContributorsLeaderboardRow key={balance.userDrn} balance={balance} />
            ))
          : null}
        {currentUser && !currentUser.isInCurrentView ? (
          <CurrentUserRankingRow user={currentUser} />
        ) : null}
      </Tbody>
    </Table>
  );
}
